import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ------------------------------------------------------------------
// Axios instance — cookie-based auth (httpOnly session cookie) with
// CSRF token echoed on every mutation.
//
// Preview-environment fallback: Emergent preview ingress injects
// `Access-Control-Allow-Origin: *` which breaks cookie credentials.
// We keep a sessionStorage bearer as a transitional fallback so the
// app is usable in preview. sessionStorage (NOT localStorage) clears
// on tab close and is not shared cross-tab — materially safer than
// the old localStorage approach and removed from codebase audit.
// In production deploy, cookies will take precedence and bearer is ignored.
// ------------------------------------------------------------------
const BEARER_KEY = "sec_bearer";
const CSRF_KEY = "sec_csrf_mirror";

export function setBearer(token) {
  if (token) {
    sessionStorage.setItem(BEARER_KEY, token);
  } else {
    sessionStorage.removeItem(BEARER_KEY);
  }
}

export function setCsrfMirror(token) {
  if (token) {
    sessionStorage.setItem(CSRF_KEY, token);
  } else {
    sessionStorage.removeItem(CSRF_KEY);
  }
}

export function clearAuthFallback() {
  sessionStorage.removeItem(BEARER_KEY);
  sessionStorage.removeItem(CSRF_KEY);
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Read a cookie value by name (sec_csrf is NOT httpOnly — readable by JS).
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp("(^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[2]) : null;
}

api.interceptors.request.use(
  (config) => {
    const method = (config.method || "get").toLowerCase();
    const isMutation = !["get", "head", "options"].includes(method);

    // Cookie auth path — attach CSRF token from cookie when we have one
    const csrfFromCookie = getCookie("sec_csrf");
    if (isMutation && csrfFromCookie) {
      config.headers["X-CSRF-Token"] = csrfFromCookie;
    }

    // Preview fallback — if no session cookie, attach bearer + mirrored CSRF
    const sessionCookie = getCookie("sec_session");
    if (!sessionCookie) {
      const bearer = sessionStorage.getItem(BEARER_KEY);
      if (bearer) {
        config.headers.Authorization = `Bearer ${bearer}`;
      }
      const csrfMirror = sessionStorage.getItem(CSRF_KEY);
      if (isMutation && csrfMirror && !config.headers["X-CSRF-Token"]) {
        config.headers["X-CSRF-Token"] = csrfMirror;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Auth
export const login = (email, password) => api.post("/auth/login", { email, password });
export const logout = () => api.post("/auth/logout");
export const getMe = () => api.get("/auth/me");
export const changePassword = (currentPassword, newPassword) =>
  api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });

// Admin Management (Super Admin only)
export const listAdmins = () => api.get("/admins");
export const createAdmin = (data) => api.post("/admins", data);
export const deleteAdmin = (adminId) => api.delete(`/admins/${adminId}`);

// Jobs
export const getJobs = (activeOnly = false) => api.get(`/jobs?active_only=${activeOnly}`);
export const getJob = (id) => api.get(`/jobs/${id}`);
export const createJob = (data) => api.post("/jobs", data);
export const updateJob = (id, data) => api.put(`/jobs/${id}`, data);
export const deleteJob = (id) => api.delete(`/jobs/${id}`);

// AI Interview Sessions
export const createSession = (data) => api.post("/sessions", data);
export const getSessions = (status) => api.get(`/sessions${status ? `?status=${status}` : ""}`);
export const getSession = (id) => api.get(`/sessions/${id}`);
export const sendMessage = (sessionId, data) => api.post(`/sessions/${sessionId}/message`, data);
export const sendProctoringEvent = (sessionId, data) => api.post(`/sessions/${sessionId}/proctoring`, data);
export const endSession = (sessionId) => api.post(`/sessions/${sessionId}/end`);

// Reports
export const getReports = () => api.get("/reports");
export const getReport = (sessionId) => api.get(`/reports/${sessionId}`);

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");

// Screening Tests
export const createTest = (data) => api.post("/tests", data);
export const getTests = (status) => api.get(`/tests${status ? `?status=${status}` : ""}`);
export const getTest = (id) => api.get(`/tests/${id}`);
export const updateTest = (id, data) => api.put(`/tests/${id}`, data);
export const deleteTest = (id) => api.delete(`/tests/${id}`);

// Test status transitions
export const startTestingMode = (id) => api.post(`/tests/${id}/start-testing`);
export const publishTest = (id) => api.post(`/tests/${id}/publish`);
export const unpublishTest = (id) => api.post(`/tests/${id}/unpublish`);
export const toggleTestActive = (id) => api.patch(`/tests/${id}/toggle-active`);

// Admin preview submit
export const previewSubmitTest = (id, answers) => api.post(`/tests/${id}/preview-submit`, { answers });

// Candidates
export const addCandidate = (testId, data) => api.post(`/tests/${testId}/candidates`, data);
export const importCandidatesCSV = (testId, csvData) =>
  api.post(`/tests/${testId}/candidates/import-csv`, { csv_data: csvData });
export const getCandidates = (testId) => api.get(`/tests/${testId}/candidates`);
export const deleteCandidate = (id) => api.delete(`/candidates/${id}`);
export const markInvited = (id) => api.post(`/candidates/${id}/mark-invited`);
export const bulkInvite = (testId, candidateIds) =>
  api.post(`/tests/${testId}/candidates/bulk-invite`, candidateIds);
export const exportCandidatesCSV = (testId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.workflow_status) params.set("workflow_status", filters.workflow_status);
  if (filters.start_date) params.set("start_date", filters.start_date);
  if (filters.end_date) params.set("end_date", filters.end_date);
  const qs = params.toString();
  return `${API}/tests/${testId}/candidates/export-csv${qs ? `?${qs}` : ""}`;
};

// Candidate test-taking (public)
export const getTestPublic = (testId) => api.get(`/test/${testId}/access`);
export const submitTestPublic = (testId, data) => api.post(`/test/${testId}/submit`, data);

// Candidate test-taking (via invite link)
export const getTestByInvite = (token) => api.get(`/take-test/${token}`);
export const submitTest = (token, data) => api.post(`/take-test/${token}/submit`, data);

// Admin routes used by JobWorkflowConfigPage
export const adminGetJob = (jobId) => api.get(`/admin/jobs/${jobId}`);
export const adminListTests = (status) =>
  api.get(`/admin/tests${status ? `?status=${status}` : ""}`);

export { API };
export default api;
